/**
 * The journey: a settled item's stage history, assembled from records that
 * already exist (YAG-372, prototype `#/w5`).
 *
 * Three real sources, no fourth:
 *   - `work_item_activity` (GET /:id/activity) for what happened and when, with
 *     `actor_name` already joined to a real person or the workspace identity;
 *   - `work_item_gates` (GET /:id/gates) for what waited on an editor, who
 *     resolved it and when — the LIST read joins the resolver's display name;
 *   - the item itself for the PR and the settle.
 *
 * The rule that shapes every line: only claim what the data shows. Older items
 * predate half of this machinery, and their journey is short rather than
 * padded. A stage with no record does not appear, and a gate whose resolver we
 * cannot name says "recorded" rather than guessing a person.
 */
import type { WorkItem, WorkItemActivityDto, WorkItemGate } from "@yagni/shared";
import { JOB_ACT_COPY, jobLineLabel, readJobGatePayload } from "@yagni/shared";
import {
  chatActTitle,
  JOURNEY_MERGE_STAYS,
  JOURNEY_RAN_ALONE,
  JOURNEY_WAITED,
  stampByLine,
} from "./workCopy.js";
import { PIPELINE_STAGES, type PipelineStage } from "./workStage.js";

export interface JourneyRow {
  key: string;
  /** The pipeline stage this row belongs to. */
  stage: PipelineStage;
  /** What happened, in one line. */
  title: string;
  /** ISO timestamp. */
  at: string;
  /** The worker whose act this was, when one is recorded. */
  personaSlug: string | null;
  /** A human actor's name, for the rows a person owns. */
  actorName: string | null;
  /** The gate marker chip: waited on an editor, or ran without waiting. */
  marker: string | null;
  /** The stamp under a resolved gate ("APPROVED · by Ami · ..."). */
  stamp: { word: string; byLine: string } | null;
  /**
   * The standing job this row belongs to ("Wright · builds"), when a Job
   * staged it (ADR-0044, plan Task 6). Null on every row a legacy path wrote,
   * and that row reads exactly as it always did.
   */
  jobLine: string | null;
  /** The act came through the ticket's chat: the timeline says so by the name. */
  fromChat: boolean;
  /** A lane session read a person's note for the next step here (YAG-1116). */
  noteRead?: true;
}

/**
 * Which pipeline stage a Job's generic act gate files under.
 *
 * `job_act` is ONE gate kind over many acts, so the stage comes from the act.
 * An act this map does not list has no row.
 */
const JOB_ACT_STAGE: Record<string, PipelineStage> = {
  push_fix: "build",
  merge: "done",
  request_changes: "review",
  send_back: "plan",
  post: "build",
};

/** Which pipeline stage a resolved gate belongs under. */
const GATE_STAGE: Record<string, PipelineStage> = {
  accept_proposal: "proposed",
  approve_execution_plan: "plan",
  needs_input: "build",
  review_result: "review",
  verify_receipt: "done",
};

const GATE_TITLE: Record<string, string> = {
  accept_proposal: "The proposal was put to an editor",
  approve_execution_plan: "The plan was put to an editor",
  needs_input: "The run stopped to ask",
  review_result: "The result was put to an editor",
  verify_receipt: "Verification landed",
};

const GATE_STAMP_WORD: Record<string, string> = {
  accept_proposal: "ACCEPTED",
  approve_execution_plan: "APPROVED",
  needs_input: "ANSWERED",
  review_result: "POSTED",
  verify_receipt: "VERIFIED",
};

/** Activity types worth a journey row, and what each one reads as. */
const ACTIVITY_ROWS: Record<string, { stage: PipelineStage; title: string }> = {
  plan_created: { stage: "plan", title: "A plan was drafted" },
  plan_recorded: { stage: "plan", title: "A plan was drafted" },
  execution_started: { stage: "build", title: "The work started" },
  result_ready: { stage: "review", title: "A result was put up for review" },
};

function recordOf(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** A gate answered from the ticket's chat records the chat as its `via`. */
function resolvedFromChat(gate: WorkItemGate): boolean {
  return recordOf(recordOf(gate.resolution)?.via)?.surface === "item_rail";
}

/**
 * The row for an act a person made from the ticket's chat, on the stage the
 * ticket stood in when it landed. An act recorded without a stage (before the
 * record carried one) has no row: the record does not say where it belongs.
 */
function chatActRow(entry: WorkItemActivityDto): JourneyRow | null {
  const stage = entry.metadata?.stage;
  if (typeof stage !== "string" || !(PIPELINE_STAGES as readonly string[]).includes(stage))
    return null;
  const title = chatActTitle(entry.metadata);
  if (!title) return null;
  return {
    key: `activity-${entry.id}`,
    stage: stage as PipelineStage,
    title,
    at: entry.createdAt,
    personaSlug: null,
    actorName: entry.actorName,
    marker: null,
    jobLine: null,
    stamp: null,
    fromChat: true,
  };
}

function shortTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Build the journey. Rows are ordered by time, because that is the only order
 * the records can prove; stages are labels on rows, not buckets that must be
 * filled.
 */
export function buildJourney(input: {
  item: WorkItem;
  activity: WorkItemActivityDto[];
  gates: WorkItemGate[];
}): JourneyRow[] {
  const rows: JourneyRow[] = [];
  // The gates that drew a row: an act from the chat that resolved one of them
  // is already on the timeline, as that gate's row.
  const gateRows = new Set<string>();
  const chatActs: WorkItemActivityDto[] = [];

  for (const entry of input.activity) {
    if (entry.type === "chat_act") {
      chatActs.push(entry);
      continue;
    }
    // A lane session took up a person's note for the next step (YAG-1116).
    if (entry.metadata?.attemptEvent === "note_read" && entry.body) {
      rows.push({
        key: `activity-${entry.id}`,
        stage: "build",
        title: entry.body,
        at: entry.createdAt,
        personaSlug: "wright",
        actorName: null,
        marker: null,
        jobLine: null,
        stamp: null,
        fromChat: false,
        noteRead: true,
      });
      continue;
    }
    const shape = ACTIVITY_ROWS[entry.type];
    if (!shape) continue;
    rows.push({
      key: `activity-${entry.id}`,
      stage: shape.stage,
      title: shape.title,
      at: entry.createdAt,
      // The Team's own machinery does not carry a worker on activity rows, so
      // the chip comes from the item. It is the honest best the records allow.
      personaSlug: entry.actorType === "human" ? null : (input.item.personaSlug ?? null),
      actorName: entry.actorType === "human" ? entry.actorName : null,
      // A run that produced work without a gate in front of it ran without
      // waiting. That is a fact about the gate record, not a claim about trust.
      marker: entry.actorType === "human" ? null : JOURNEY_RAN_ALONE,
      jobLine: null,
      stamp: null,
      fromChat: false,
    });
  }

  for (const gate of input.gates) {
    if (gate.status === "open") continue;
    // A Job's act decides its own node and its own stamp: `job_act` is one
    // gate kind over many acts, and filing every one of them under a single
    // stage would put a merge on the implementing beat. Before this, a
    // resolved `job_act` gate had no node at all and fell out of the record.
    const job = readJobGatePayload(gate.payload);
    const stage = GATE_STAGE[gate.kind] ?? (job?.act ? JOB_ACT_STAGE[job.act] : null);
    if (!stage) continue;
    const resolvedAt = gate.resolvedAt ?? gate.openedAt;
    gateRows.add(gate.id);
    rows.push({
      key: `gate-${gate.id}`,
      stage,
      title: GATE_TITLE[gate.kind] ?? gate.title,
      at: resolvedAt,
      personaSlug: null,
      actorName: gate.resolvedByName ?? null,
      marker: JOURNEY_WAITED,
      jobLine: job?.job
        ? jobLineLabel({
            agentName: job.job.agentName,
            kind: job.job.kind,
            instance: job.job.instance,
          })
        : null,
      stamp:
        gate.status === "resolved"
          ? {
              word:
                (job?.act ? JOB_ACT_COPY[job.act]?.stamp : null) ??
                GATE_STAMP_WORD[gate.kind] ??
                "RESOLVED",
              byLine: stampByLine(gate.resolvedByName ?? null, shortTime(resolvedAt)),
            }
          : null,
      fromChat: resolvedFromChat(gate),
    });
  }

  for (const entry of chatActs) {
    const gateId = entry.metadata?.gateId;
    if (typeof gateId === "string" && gateRows.has(gateId)) continue;
    const row = chatActRow(entry);
    if (row) rows.push(row);
  }

  // The merge row exists only when there IS a pull request. It carries the one
  // line that is true regardless of any Team's earned level: merging is not
  // delegated, so it never waited on a worker and never will.
  if (input.item.prUrl) {
    rows.push({
      key: "pr",
      stage: "done",
      title:
        typeof input.item.prNumber === "number"
          ? `Pull request #${input.item.prNumber}`
          : "A pull request was opened",
      at: input.item.updatedAt,
      personaSlug: input.item.personaSlug ?? null,
      actorName: null,
      marker: JOURNEY_MERGE_STAYS,
      jobLine: null,
      stamp: null,
      fromChat: false,
    });
  }

  return rows.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

export { shortTime as journeyTime };
